import './App.css';
import { Suspense, useEffect, useRef, useCallback } from 'react';
import { Route, BrowserRouter as Router, Routes, useLocation } from 'react-router-dom';
import { WorkspaceProvider, useWorkspace } from './context/WorkspaceContext';
import { authApi } from './api/api';
import AuthProvider from './components/AuthProvider';
import ChunkErrorBoundary from './components/ChunkErrorBoundary';
import Navigation from './components/Navigation';
import { lazyWithReload } from './utils/lazyLoad';
import { initGA, trackPageView } from './utils/analytics';

const Login = lazyWithReload(() => import('./pages/Login'));
const Signup = lazyWithReload(() => import('./pages/Signup'));
const PrivacyPolicy = lazyWithReload(() => import('./pages/PrivacyPolicy'));
const Dashboard = lazyWithReload(() => import('./pages/Dashboard'));
const JobPosting = lazyWithReload(() => import('./pages/JobPosting'));
const ResumeScreening = lazyWithReload(() => import('./pages/ResumeScreening'));
const InterviewScreening = lazyWithReload(() => import('./pages/InterviewScreening'));
const WorkTrialScreening = lazyWithReload(() => import('./pages/WorkTrialScreening'));
const WorkTrialInterviewAnalytics = lazyWithReload(() => import('./pages/WorkTrialInterviewAnalytics'));
const MotivationAssessment = lazyWithReload(() => import('./pages/MotivationAssessment'));
const RiskAssessment = lazyWithReload(() => import('./pages/RiskAssessment'));
const CandidateComparison = lazyWithReload(() => import('./pages/CandidateComparison'));
const Hire = lazyWithReload(() => import('./pages/Hire'));
const Applications = lazyWithReload(() => import('./pages/Applications'));
const WorkTrialManagement = lazyWithReload(() => import('./pages/WorkTrialManagement'));
const WorkTrialDetail = lazyWithReload(() => import('./pages/WorkTrialDetail'));
const ApplicationDetail = lazyWithReload(() => import('./pages/ApplicationDetail'));
const Interviews = lazyWithReload(() => import('./pages/Interviews'));
const Inbox = lazyWithReload(() => import('./pages/Inbox'));
const Settings = lazyWithReload(() => import('./pages/Settings'));
const EmailManagement = lazyWithReload(() => import('./pages/EmailManagement'));
const Account = lazyWithReload(() => import('./pages/Account'));
const StageScreen = lazyWithReload(() => import('./pages/StageScreen'));
const Sourcing = lazyWithReload(() => import('./pages/Sourcing'));
// Dev-only; the conditional lets webpack drop the chunk from production builds
const NavPreview = process.env.NODE_ENV === 'development'
    ? lazyWithReload(() => import('./pages/NavPreview'))
    : null;

// Initialize GA4 once when the module loads
initGA();

// Component to track page views on route changes
function PageViewTracker() {
    const location = useLocation();

    useEffect(() => {
        trackPageView(location.pathname + location.search);
    }, [location]);

    return null;
}

// Syncs the workspace title based on the current job position route
function WorkspaceSync() {
    const location = useLocation();
    const { setTitle } = useWorkspace();
    const cache = useRef({});
    const lastId = useRef(null);

    const fetchTitle = useCallback(async (id) => {
        if (cache.current[id]) {
            setTitle(cache.current[id]);
            return;
        }
        // Clear the previous position's title so the sidebar shows its
        // loading skeleton instead of stale data while the new one loads
        setTitle(null);
        try {
            const formData = new FormData();
            formData.append('job_position_id', id);
            const response = await authApi.post('/job-position/info', formData);
            const title = response.data?.job_position?.title;
            if (title) {
                cache.current[id] = title;
                // Ignore responses that arrive after another navigation
                if (lastId.current === id) {
                    setTitle(title);
                }
            }
        } catch {
            // Silently fail - title will just not show
        }
    }, [setTitle]);

    useEffect(() => {
        const match = location.pathname.match(/^\/job-position\/([^/]+)/);
        if (match) {
            const id = match[1];
            if (id !== lastId.current) {
                lastId.current = id;
                fetchTitle(id);
            }
        } else {
            if (lastId.current) {
                lastId.current = null;
                setTitle(null);
            }
        }
    }, [location.pathname, setTitle, fetchTitle]);

    return null;
}

function RouteFallback() {
    return (
        <div className="route-loading">
            <div className="route-loading-spinner" />
        </div>
    );
}

function AppRoutes() {
    const location = useLocation();

    return (
        <ChunkErrorBoundary pathname={location.pathname}>
            <Suspense fallback={<RouteFallback />}>
                <Routes>
                    <Route path="/" element={<Dashboard/>}/>
                    <Route path="/account" element={<Account/>}/>
                    <Route path="/login" element={<Login/>}/>
                    <Route path="/signup" element={<Signup/>}/>
                    <Route path="/privacy-policy" element={<PrivacyPolicy/>}/>
                    <Route path="/job-position/:jobPositionId/job-posting" element={<JobPosting/>}/>
                    <Route path="/job-position/:jobPositionId/sourcing" element={<Sourcing/>}/>
                    <Route path="/job-position/:jobPositionId/resume-screening" element={<ResumeScreening/>}/>
                    <Route path="/job-position/:jobPositionId/interview-screening" element={<InterviewScreening/>}/>
                    <Route path="/job-position/:jobPositionId/work-trial-screening" element={<WorkTrialScreening/>}/>
                    <Route path="/job-position/:jobPositionId/hire" element={<Hire/>}/>
                    {/* Dynamic Stage Route for custom pipeline stages */}
                    <Route path="/job-position/:jobPositionId/stage/:stageSlug" element={<StageScreen/>}/>
                    {/* Management Routes */}
                    <Route path="/job-position/:jobPositionId/applications" element={<Applications/>}/>
                    <Route path="/job-position/:jobPositionId/application/:userId" element={<ApplicationDetail/>}/>
                    <Route path="/job-position/:jobPositionId/work-trials" element={<WorkTrialManagement/>}/>
                    <Route path="/job-position/:jobPositionId/work-trial/:userId" element={<WorkTrialDetail/>}/>
                    <Route path="/job-position/:jobPositionId/interviews" element={<Interviews/>}/>
                    <Route path="/job-position/:jobPositionId/inbox" element={<Inbox/>}/>
                    <Route path="/job-position/:jobPositionId/email-management" element={<EmailManagement/>}/>
                    <Route path="/job-position/:jobPositionId/settings" element={<Settings/>}/>
                    {/* Analytics Routes */}
                    <Route path="/job-position/:jobPositionId/interview-analytics" element={<WorkTrialInterviewAnalytics/>}/>
                    <Route path="/job-position/:jobPositionId/motivation-assessment" element={<MotivationAssessment/>}/>
                    <Route path="/job-position/:jobPositionId/risk-assessment" element={<RiskAssessment/>}/>
                    <Route path="/job-position/:jobPositionId/compare" element={<CandidateComparison/>}/>
                    {/* Dev-only playground for the unified sidebar */}
                    {process.env.NODE_ENV === 'development' && (
                        <Route path="/nav-preview" element={<NavPreview/>}/>
                    )}
                </Routes>
            </Suspense>
        </ChunkErrorBoundary>
    );
}

function App() {
    return (
        <Router>
            <WorkspaceProvider>
            <PageViewTracker />
            <AuthProvider>
                <WorkspaceSync />
                {/* Global sidebar — outside Suspense so it persists across
                    route transitions and its animations stay smooth */}
                <Navigation />
                <AppRoutes />
            </AuthProvider>
            </WorkspaceProvider>
        </Router>
    );
}

export default App;
